/**
 * The published legal documents: where they come from and where they are read.
 *
 * The source of truth is the plain text in `docs/legal/`, which is what an
 * attorney reviews and what `docs/legal/BEFORE-PUBLISHING.txt` is a checklist
 * for. The HTML under `/legal/` is BUILT from it (see `vite.legal.ts`), so the
 * published page cannot drift from the reviewed document -- there is no second
 * copy to forget to update.
 *
 * Named here rather than as literals at each call site because four things must
 * agree about the same two addresses: the build that writes the files, the two
 * proxies that route the prefix, the registration checkbox that links them, and
 * every screen that offers them afterwards.
 *
 * NOT under an app's prefix. `/legal/terms.html` is served at the ORIGIN ROOT,
 * like `/`, because it must be readable by a stranger who has no account and by
 * somebody halfway through registering -- and because the Policy describes
 * collection that begins AT registration, so it has to be reachable before it.
 *
 * A plain `.html` file rather than a route in an app: it is read once, possibly
 * on a desktop that has never seen this platform, and it should not need a
 * bundle, a session or a service worker to render a document.
 */

export interface LegalDocument {
  /** The file under `docs/legal/`, which is the reviewed original. */
  source: string;
  /** Where the built page lands, relative to the site build's output. */
  file: string;
  /** The address it is read at. Same origin, absolute, no app prefix. */
  path: string;
  /**
   * The same document as PLAIN TEXT, beside the page.
   *
   * For a reader that supplies its own frame -- the app's LegalPane, which
   * shows the text inside a full-screen pane it styles itself. The page above
   * carries a heading, a link row and its own margins, all of which are the
   * right thing for a stranger's browser tab and wrong inside a pane that
   * already has a bar naming the document and a Close on it. Emitted by the
   * same build from the same source, so it is still one document of record.
   */
  textFile: string;
  textPath: string;
  /** The `<title>`, and how the document is named in a link. */
  title: string;
}

/**
 * The Terms and the Policy, and DELIBERATELY NOT `BEFORE-PUBLISHING.txt`, which
 * says of itself: "Delete it, or keep it out of whatever is published -- it is
 * for you." It is a working checklist naming every unresolved placeholder and
 * every conflict between the drafts and the code, which is the last thing to
 * publish beside the documents it criticises.
 */
export const LEGAL_DOCUMENTS: readonly LegalDocument[] = [
  {
    source: 'TERMS-OF-SERVICE.txt',
    file: 'legal/terms.html',
    path: '/legal/terms.html',
    textFile: 'legal/terms.txt',
    textPath: '/legal/terms.txt',
    title: 'Terms of Service',
  },
  {
    source: 'PRIVACY-POLICY.txt',
    file: 'legal/privacy.html',
    path: '/legal/privacy.html',
    textFile: 'legal/privacy.txt',
    textPath: '/legal/privacy.txt',
    title: 'Privacy Policy',
  },
] as const;

/**
 * BOTH DOCUMENTS ARE AGREED TO BY ONE ACT, and the Terms say so themselves.
 *
 * The Policy "forms part of, and is incorporated by reference into, the Terms",
 * and the Terms' preamble recites agreement "TO BE BOUND BY THIS AGREEMENT AND
 * BY THE PRIVACY POLICY, WHICH IS INCORPORATED HEREIN BY REFERENCE". So the
 * registration form asks once and links twice: two checkboxes would suggest the
 * Policy is separately declinable, which it is not, and would leave whoever
 * ticked only one in a state neither document describes.
 */
export const LEGAL_TERMS_PATH = '/legal/terms.html';
export const LEGAL_PRIVACY_PATH = '/legal/privacy.html';
